import { useForm } from "react-hook-form";
import { useState, useEffect, useRef, useMemo } from "react";
import Modal from "./Modal";
import ModalRemove from "./ModalRemove";
import { useDispatch, useSelector } from "react-redux";
import {
  ObtenerTiposPeriodos,
  ActualizarTipoPeriodo,
  EliminarTipoPeriodo,
  InsertarTipoPeriodo,
} from "../../redux/periodosSlice";
import WindowDimension from "../../Hooks/WindowDimension";
import Required from "../../Components/Required";
import useSubmit from "../../Hooks/useSubmit";
import { OrderTable } from "../../Components/OrderTable";
import { getAdminPeriodTypesPage } from "../../Api/content";
import { useLocale } from "../../i18n/localeContext";
import { Skeleton } from "antd";
import { StrapiError } from '../../Inicio/Pages/StrapiError';
import { _translateValue } from "../translation-gestor/translateValue";

import { useValidation } from "../../Hooks/useValidation";

const SCREEN_NAME = "PantallaTiposPeriodos";
function TiposPeriodos(props) {
  const { validation } = useValidation();
  const { locale } = useLocale();
  const [adminPeriodTypesPage, setAdminPeriodTypesPage] = useState(null);
  const [isLoadingContent, setIsLoadingContent] = useState(true);
  const [translatedNombres, setTranslatedNombres] = useState({});
  const [isTranslating, setIsTranslating] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [tituloModal, setTituloModal] = useState("Agregar");
  const [removeState, changeRemoveState] = useState(false);
  const [itemSelected, setItemSelected] = useState({});
  const { width } = WindowDimension();

  const dispatch = useDispatch();
  const { submitAction } = useSubmit();

  let busqueda = useRef();

  const { tiposPeriodos } = useSelector((state) => state.periodos);

  const {
    register,
    formState: { errors },
    handleSubmit,
    reset,
    setValue,
  } = useForm();

  const columns = useMemo(() => {
    const table = adminPeriodTypesPage?.periodtype_table ?? [];
    return [
      { label: table[0]?.label || "Código", accessor: "periodoTipoId", sortable: true },
      { 
        label: table[1]?.label || "Nombre", 
        accessor: "periodoTipoNombre", 
        sortable: true,
        render: (item) => (
          <td>{translatedNombres[item.periodoTipoNombre] || item.periodoTipoNombre}</td>
        ),
      },
      { label: table[2]?.label || "Acción", accessor: "action", sortable: false },
    ];
  }, [adminPeriodTypesPage, translatedNombres]);

  const showModalEdit = (item) => {
    reset(item);
    setTituloModal(adminPeriodTypesPage?.subtitleO || "Editar");
    setShowModal(true);
  };

  const closeModalEdit = () => {
    reset({
      periodoTipoId: undefined,
      periodoTipoNombre: "",
      estadoId: undefined,
    });
    setShowModal(false);
  };

  const showRemoveModalEdit = (item) => {
    setItemSelected(item);
    changeRemoveState(true);
  };

  const closeRemoveModalEdit = () => {
    setItemSelected({});
    changeRemoveState(false);
  };

  const cargarTiposPeriodos = () => {
    dispatch(ObtenerTiposPeriodos());
  };

  const cargarBusqueda = () => {
    dispatch(ObtenerTiposPeriodos(busqueda.current.value));
  };

  const eliminar = async () => {
    submitAction(EliminarTipoPeriodo, itemSelected, () => {
      closeRemoveModalEdit();
      cargarTiposPeriodos();
    });
  };

  const onSubmit = async (data) => {
    submitAction(
      data.periodoTipoId > 0 ? ActualizarTipoPeriodo : InsertarTipoPeriodo,
      data,
      () => {
        closeModalEdit();
        cargarTiposPeriodos();
      }
    );
  };

  useEffect(() => {
    cargarTiposPeriodos();
  }, [dispatch]);

  useEffect(() => {
    let cancel = false;
    setIsLoadingContent(true);
    getAdminPeriodTypesPage(locale)
      .then((res) => {
        const data = res?.data?.attributes || res?.data || res;
        if (!cancel) {
          setAdminPeriodTypesPage(data);
          setIsLoadingContent(false);
        }
      })
      .catch(() => { 
        if (!cancel) {
          setAdminPeriodTypesPage(null);
          setIsLoadingContent(false);
        }
      });
    return () => { cancel = true };
  }, [locale])

  // Efecto para traducir el campo "Nombre" cuando lleguen los datos o cambie el locale
  useEffect(() => {
    const translateData = async () => {
      // Verificar si hay datos para traducir
      const hasTiposPeriodos = tiposPeriodos && Array.isArray(tiposPeriodos) && tiposPeriodos.length > 0;

      // Si no hay datos, no hacer nada
      if (!hasTiposPeriodos) {
        setIsTranslating(false);
        return;
      }

      // Iniciar traducción
      setIsTranslating(true);

      const nombreTranslations = {};

      // Traducir solo el campo "Nombre"
      const translationPromises = tiposPeriodos.map(async (item) => {
        if (item.periodoTipoNombre) {
          const translated = await _translateValue(SCREEN_NAME, item.periodoTipoNombre, locale);
          nombreTranslations[item.periodoTipoNombre] = translated;
        }
      });
      await Promise.all(translationPromises);

      // Actualizar estados y finalizar traducción
      setTranslatedNombres(nombreTranslations);
      setIsTranslating(false);
    };

    translateData();
  }, [tiposPeriodos, locale]);

  if (isLoadingContent || isTranslating) {
    return (
      <div className="main-admin">
        <div className="main-admin-bar">
          <div className="header-admin-bar-items">
            <Skeleton.Button style={{ width: 40, height: 40 }} active />
            <Skeleton.Input style={{ width: 300, height: 30 }} active />
          </div>
          <div className="header-admin-bar-items ml-auto">
            <Skeleton.Input style={{ width: 200, height: 40 }} active />
          </div>
        </div>
        <div className="table-container">
          <Skeleton.Input style={{ width: '100%', height: 400, minWidth: '800px' }} active />
        </div>
      </div>
    );
  }

  return (
    <div className="main-admin">
      <div className="main-admin-bar">
        <div className="header-admin-bar-items">
          <button
            className="main-admin-button"
            onClick={() => {
              setShowModal(true);
              setTituloModal(adminPeriodTypesPage?.subtitleTR || "Agregar");
              setValue("tipo", "");
              reset();
            }}
          >
            +
          </button>
          <h2>{adminPeriodTypesPage?.title || "Tipos de periodos"}</h2>
        </div>

        <div className="header-admin-bar-items ml-auto">
          <input
            type="search"
            className="form-control"
            onInput={cargarBusqueda}
            ref={busqueda}
            placeholder={adminPeriodTypesPage?.search_input || "Búsqueda"}
          />
        </div>
      </div>

      <div className="table-container">
        {tiposPeriodos?.length > 0 ? (
          <OrderTable
            {...{ columns }}
            data={tiposPeriodos}
            clase="table-adm"
            actions={[
              {
                icon: "",
                clase: "text-primary",
                style:{display:"contents"},
                event: (item) => showModalEdit(item),
              },
              {
                icon: "",
                clase: "delete",
                style:{display:"contents"},
                event: (item) => showRemoveModalEdit(item),
              },
            ]}
           
          />
        ) : (
          <div className="no-results-container">
            <img src={props.img} alt="Imagen sin resultados" />
            <h1>{props.noResultMessage}</h1>
          </div>
        )}
      </div>

      <Modal
        estado={showModal}
        cambiarEstado={closeModalEdit}
        titulo={tituloModal}
        clase={`${width < 576 ? "full-width" : "small"}`}
        buttonGroup={
          <>
            <button
              type="submit"
              className="button-text"
              onClick={handleSubmit(onSubmit)}
            >
              {adminPeriodTypesPage?.save_button || "Guardar"}
            </button>
          </>
        }
      >
        <form>
          <div className="form-group">
            <label>{adminPeriodTypesPage?.periodtype || "Tipo de periodo"}</label>
            <Required />
            <input
              className="form-control"
              name="periodoTipoNombre"
              {...register("periodoTipoNombre", {
                required: validation?.required_field || "Campo requerido",
              })}
            />
            {errors.periodoTipoNombre && (
              <p className="error">{errors.periodoTipoNombre.message}</p>
            )}
          </div>
        </form>
      </Modal>

      <ModalRemove
        estado={removeState}
        closeModalHandled={closeRemoveModalEdit}
        title={adminPeriodTypesPage?.subtitleT || "Eliminar plantillla"}
        message={adminPeriodTypesPage?.question || "¿Seguro que desea eliminar el siguiente registro?"}
        item={itemSelected.periodoTipoNombre}
        btnGroup={
          <>
            <button className="button-text text-red" onClick={eliminar}>
              {adminPeriodTypesPage?.confirm_button || "Confirmar"}
            </button>
            <button
              className="button-text text-grey"
              onClick={() => {
                closeRemoveModalEdit(false);
              }}
            >
              {adminPeriodTypesPage?.cancel_button || "Cancelar"}
            </button>
          </>
        }
      />
    </div>
  );
}

export default TiposPeriodos;
